/**
 * Helpers for a driver's preferred name.
 *
 * A driver can have an optional `preferredName` (a preferred first name). When it is empty the
 * legal first name is used. These helpers are used by the dispatch pages, the assign-driver modal
 * and the driver note emails so the preferred first name is shown there.
 */

export interface DriverNameParts {
  name?: string | null;
  lastName?: string | null;
  preferredName?: string | null;
}

/** The first name to show: the preferred name when set, otherwise the legal first name. */
export function getDriverFirstName(driver?: DriverNameParts | null): string {
  const preferred = (driver?.preferredName ?? "").trim();
  return preferred || (driver?.name ?? "").trim();
}

/** "First Last" using the preferred first name when set. */
export function getDriverDisplayName(driver?: DriverNameParts | null): string {
  if (!driver) return "";
  return `${getDriverFirstName(driver)} ${driver.lastName ?? ""}`.trim();
}

/** "Last, First" using the preferred first name when set. */
export function getDriverDisplayNameReversed(driver?: DriverNameParts | null): string {
  if (!driver) return "";
  const first = getDriverFirstName(driver);
  const last = (driver.lastName ?? "").trim();
  if (first && last) return `${last}, ${first}`;
  return first || last;
}

/**
 * "Last First" for a name that only arrives as one "First Last" string, which is how the server
 * builds team member names. Only a two word name is swapped: a longer one may be a compound surname
 * ("Mary Jo Van Dyke") and guessing which words belong together would mangle it, so it is left as
 * it came.
 */
export function getLastFirstName(fullName?: string | null): string {
  const parts = (fullName ?? "")
    .trim()
    .split(/\s+/)
    .filter((part) => !!part);
  if (parts.length !== 2) return parts.join(" ");
  return `${parts[1]} ${parts[0]}`;
}
